import React, { useState, useContext, useEffect } from 'react'
import GooglePlacesAutocomplete, { geocodeByAddress } from 'react-google-places-autocomplete'
import get from 'lodash/get'
import './styles.scss'

import contractContext from '../../../../../store/contexts/contractContext'
import CONTRACT_ACTIONS from '../../../../../store/actions/contractActions'

import GeneralInput from '../../../../../components/GeneralInput'
import Button from '../../../../../components/Button'
import PropertyCard from '../../../../../components/PropertyCard'
import LocaleSelect from '../../../../../components/LocaleSelect'
import RoundedButtonsRow from 'components/RoundedButtonsRow'
import PropertyTypeMultiSelect from 'components/PropertyTypeMultiSelect'
import ErrorComponent from 'components/ErrorComponent'

import { getUserAnnouncements } from '../../../../../services/announcements'

import { useCommonStyles } from 'styles/globalStyles'
import { PropertyAnnouncement } from 'types/propertyAnnouncement'
import { scrollToElement } from 'helpers/scrollTo'
import { getFormattedAddressDetails } from 'helpers/address'

type FormItem = {
  label: string
  value: string | number
  error?: string
  mandatory?: boolean
}

type PropertyDataForm = {
  address: {
    city: FormItem
    complement: FormItem
    neighbourhood: FormItem
    number: FormItem
    state: FormItem
    street: FormItem
    zip_code: FormItem
  }
  info: {
    type: FormItem
    area: FormItem
  }
  location: {
    latitude: FormItem
    longitude: FormItem
  }
  location_deltas: {
    latitude: FormItem
    longitude: FormItem
  }
}

const INITIAL_FORM_VALUES = {
  address: {
    city: { label: 'Cidade', value: '', error: '', mandatory: true },
    complement: { label: 'Complemento', value: '', error: '' },
    neighbourhood: { label: 'Bairro', value: '', error: '', mandatory: true },
    number: { label: 'Número', value: '' },
    state: { label: 'Estado', value: '', error: '', mandatory: true },
    street: { label: 'Nome da rua', value: '', error: '', mandatory: true },
    zip_code: { label: 'CEP', value: '', error: '', mandatory: true },
  },
  info: {
    type: { label: 'Tipo de Propriedade', value: 'apartment', error: '', mandatory: true },
    area: { label: 'Área', value: '', error: '', mandatory: true },
  },
  location: {
    latitude: { label: 'Lat', value: 0 },
    longitude: { label: 'Lng', value: 0 },
  },
  location_deltas: {
    latitude: { label: 'LatDelta', value: 0 },
    longitude: { label: 'LngDelta', value: 0 },
  },
}

export default function ImmobileData() {
  const commonClasses = useCommonStyles()
  const [registerPropertyData, setRegisterPropertyData] = useState<PropertyDataForm>(INITIAL_FORM_VALUES)
  const [ownedProperties, setOwnedProperties] = useState(0)
  const [carSlots, setCarSlots] = useState(1)
  const [bathrooms, setBathrooms] = useState(1)
  const [rooms, setRooms] = useState(1)
  const [propertyCards, setPropertyCards] = useState<PropertyAnnouncement[]>([])
  const [isRegistering, setIsRegistering] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState('')
  const [error, setError] = useState<string[]>([])

  const setPropertyData = (editedItem: keyof PropertyDataForm, key: string, value: string | number) => {
    setRegisterPropertyData((prevState) => ({
      ...prevState,
      [editedItem]: {
        ...prevState[editedItem],
        [key]: {
          ...prevState[editedItem][key],
          value,
        },
      },
    }))
    if (registerPropertyData[editedItem][key]?.error) {
      setPropertyDataError(editedItem, key, '')
    }
  }

  const {
    contract,
    handleStepChange,
    auth: {
      userData: { accessToken, id },
    },
    setIsLoading,
  } = useContext(contractContext)

  const getInitialData = async () => {
    const { data, total } = contract.properties

    if (Boolean(contract.properties.data.length)) {
      setPropertyCards(data)
      setOwnedProperties(total)
    }

    if (get(contract, 'address.street')) {
      const { type, area, rooms, car_slots, bathrooms } = contract.info
      const { neighbourhood, complement, number, street, zip_code, city, state } = contract.address

      setPropertyData('address', 'type', type)
      setPropertyData('address', 'area', area)
      setCarSlots(car_slots)
      setBathrooms(bathrooms)
      setRooms(rooms)
      setPropertyData('address', 'neighbourhood', neighbourhood)
      setPropertyData('address', 'complement', complement)
      setPropertyData('address', 'number', number)
      setPropertyData('address', 'address', street)
      setPropertyData('address', 'zip_code', zip_code)
      setPropertyData('address', 'city', city)
      setPropertyData('address', 'state', state)

      setIsRegistering(true)

      return
    }
  }

  const fetchProperties = async () => {
    if (id && accessToken) {
      setIsLoading(true)
      try {
        const { data, total } = await getUserAnnouncements(id, accessToken, propertyCards.length)

        generatePropertyCards(data, total)
        getInitialData()
      } catch (err) {}
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchProperties()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const generatePropertyCards = (properties: PropertyAnnouncement[], total: number) => {
    const formattedProperties = properties.map(({ id, address, info }) => ({
      id,
      address,
      info,
    }))

    if (!ownedProperties) {
      setOwnedProperties(total)
      contract.contractDispatch({
        type: CONTRACT_ACTIONS.SET_OWNED_PROPERTIES,
        value: total,
      })
    }

    if (!contract.properties.fetched) {
      contract.contractDispatch({
        type: CONTRACT_ACTIONS.SET_INITIAL_FETCH,
        value: true,
      })
    }

    const newPropertyCards = [...propertyCards, ...formattedProperties]

    //@ts-ignore
    setPropertyCards(newPropertyCards)

    contract.contractDispatch({
      type: CONTRACT_ACTIONS.SET_PROPERTIES,
      value: newPropertyCards,
    })
  }

  const getDataFromForm = () => {
    return {
      address: {
        neighbourhood: registerPropertyData.address.neighbourhood.value,
        number: registerPropertyData.address.number.value,
        street: registerPropertyData.address.street.value,
        complement: registerPropertyData.address.complement.value,
        zip_code: registerPropertyData.address.zip_code.value,
        city: registerPropertyData.address.city.value,
        state: registerPropertyData.address.state.value,
      },
      info: {
        type: registerPropertyData.info.type.value,
        area: registerPropertyData.info.area.value,
        rooms,
        bathrooms,
        car_slots: carSlots,
      },
      location: {
        latitude: registerPropertyData.location.latitude.value,
        longitude: registerPropertyData.location.longitude.value,
      },
      location_deltas: {
        latitude: registerPropertyData.location_deltas.latitude.value,
        longitude: registerPropertyData.location_deltas.longitude.value,
      },
    }
  }

  const getDataFromPropertyCards = () => {
    const propertyFound = propertyCards.find(({ id }) => selectedProperty === id)
    if (!propertyFound) {
      return null
    }

    return {
      address: propertyFound.address,
      info: propertyFound.info,
      location: propertyFound.location,
      location_deltas: propertyFound.location_deltas,
    }
  }

  const setPropertyDataError = (key: keyof PropertyDataForm, key2: string, error: string) => {
    setRegisterPropertyData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [key2]: {
          ...prev[key][key2],
          error,
        },
      },
    }))
  }

  const validateForm = () => {
    let isValid = true
    Object.keys(registerPropertyData).forEach((property) => {
      Object.keys(registerPropertyData[property]).forEach((key) => {
        if (registerPropertyData[property][key].mandatory && !registerPropertyData[property][key].value) {
          setPropertyDataError(
            property as keyof PropertyDataForm,
            key,
            `${registerPropertyData[property][key].label} é um campo obrigatório`,
          )
          isValid = false
        }
      })
    })
    return isValid
  }

  const handleRegisterData = () => {
    const propertyData = isRegistering ? getDataFromForm() : getDataFromPropertyCards()
    if (!propertyData) {
      scrollToElement('ImmobileData')
      setError(['Por favor selecione ou cadastre um imóvel.'])
      return
    }

    if (isRegistering) {
      const isValid = validateForm()

      if (!isValid) {
        scrollToElement('ImmobileData')
        return
      }
    }

    contract.contractDispatch({
      type: CONTRACT_ACTIONS.SET_CONTRACT_INFO,
      value: propertyData,
    })
    contract.contractDispatch({
      type: CONTRACT_ACTIONS.SET_CONTRACT_ID,
      value: selectedProperty,
    })

    handleStepChange('NegotiatedConditions')
  }

  const handleTypeSelect = (type: string) => {
    setPropertyData('info', 'type', type)
  }

  const handlePropertySelect = (propertyId) => {
    setSelectedProperty(propertyId)
  }

  const handleStateSelect = (state: string) => {
    setPropertyData('address', 'state', state)
  }

  const onSelectAddress = (location) => {
    geocodeByAddress(location.label).then((results) => {
      const { coordinates, ...formattedAddress } = getFormattedAddressDetails(results[0])
      setPropertyData('location', 'latitude', coordinates?.latitude || 0)
      setPropertyData('location', 'longitude', coordinates?.longitude || 0)
      setPropertyData('location_deltas', 'latitude', coordinates?.latitudeDelta || 0)
      setPropertyData('location_deltas', 'longitude', coordinates?.longitudeDelta || 0)

      !!formattedAddress.city && setPropertyData('address', 'city', formattedAddress.city)
      !!formattedAddress.neighbourhood &&
        setPropertyData('address', 'neighbourhood', formattedAddress.neighbourhood)
      !!formattedAddress.number && setPropertyData('address', 'number', formattedAddress.number)
      !!formattedAddress.state && setPropertyData('address', 'state', formattedAddress.state)
      !!formattedAddress.street && setPropertyData('address', 'street', formattedAddress.street)
      !!formattedAddress.zip_code && setPropertyData('address', 'zip_code', formattedAddress.zip_code)
    })
  }

  const {
    address: { state, city, street, neighbourhood, number, complement, zip_code },
    info: { type, area },
  } = registerPropertyData

  return (
    <div className='immobile-data-container'>
      <p className={commonClasses.contractSubtitle}>
        Defina as caracteristicas do seu imóvel que serão anexadas ao contrato de aluguel
      </p>
      <ErrorComponent errors={error} />
      {isRegistering ? (
        <>
          <div style={{ marginBottom: 16 }} />
          <PropertyTypeMultiSelect onClick={handleTypeSelect} propertyTypes={[type.value as string]} />
          <GeneralInput
            label='Área do imóvel'
            value={area.value}
            handleChange={(e) => setPropertyData('info', 'area', e.target.value)}
            error={area.error}
          />

          <div className='round-buttons-container'>
            <span className='round-buttons-label'>Quartos</span>
            <RoundedButtonsRow
              type='rooms'
              hasDoesntMatter={false}
              min={1}
              max={5}
              selectedValue={rooms}
              setValue={(label, number) => setRooms(number)}
            />
          </div>
          <div className='round-buttons-container'>
            <span className='round-buttons-label'>Banheiros</span>
            <RoundedButtonsRow
              type='bathrooms'
              hasDoesntMatter={false}
              min={1}
              max={5}
              selectedValue={bathrooms}
              setValue={(label, number) => setBathrooms(number)}
            />
          </div>
          <div className='round-buttons-container'>
            <span className='round-buttons-label'>Vagas de garagem</span>
            <RoundedButtonsRow
              type='carSlots'
              hasDoesntMatter={false}
              hasPlusSign
              min={0}
              max={4}
              selectedValue={carSlots}
              setValue={(label, number) => setCarSlots(number)}
            />
          </div>

          <div style={{ margin: '16px 0 32px' }}>
            <p style={{ marginBottom: 16 }}>Procurar endereço</p>
            <GooglePlacesAutocomplete
              selectProps={{
                onChange: onSelectAddress,
                placeholder: 'Digite o endereço do imóvel',
                loadingMessage: () => 'Carregando...',
                noOptionsMessage: () => '',
              }}
            />
          </div>
          <LocaleSelect onChange={handleStateSelect} value={state.value as string} error={state.error} />
          <GeneralInput
            label='CEP'
            value={zip_code.value}
            handleChange={(e) => setPropertyData('address', 'zip_code', e.target.value)}
            error={zip_code.error}
          />
          <GeneralInput
            label='Cidade'
            value={city.value}
            handleChange={(e) => setPropertyData('address', 'city', e.target.value)}
            error={city.error}
          />
          <GeneralInput
            label='Rua'
            value={street.value}
            handleChange={(e) => setPropertyData('address', 'street', e.target.value)}
            error={street.error}
          />

          <GeneralInput
            label='Bairro'
            value={neighbourhood.value}
            handleChange={(e) => setPropertyData('address', 'neighbourhood', e.target.value)}
            error={neighbourhood.error}
          />

          <GeneralInput
            handleChange={(e) => setPropertyData('address', 'number', e.target.value)}
            value={number.value}
            label='Número'
          />
          <GeneralInput
            label='Complemento'
            value={complement.value}
            handleChange={(e) => setPropertyData('address', 'complement', e.target.value)}
          />
        </>
      ) : (
        <div className='cards-container'>
          {propertyCards.map(({ id, address, info }) => {
            return (
              <div className='card-spacer' key={id}>
                <PropertyCard
                  selected={id === selectedProperty}
                  id={id}
                  address={address}
                  info={info}
                  onClick={handlePropertySelect}
                />
              </div>
            )
          })}
          {ownedProperties > propertyCards.length && (
            <Button title='Carregar mais' onClick={fetchProperties} />
          )}
        </div>
      )}

      <div className='end-aligner'>
        <div className='button-footer'>
          <Button
            title={isRegistering ? 'Selecionar Existente' : 'CADASTRAR'}
            onClick={() => setIsRegistering(!isRegistering)}
          />
          <Button title={isRegistering ? 'CADASTRAR' : 'CONTINUAR'} onClick={handleRegisterData} />
        </div>
      </div>
    </div>
  )
}
